// cheerSfx.js — tiny Web Audio "crowd roar" and "opponent groan" synthesiser.
//
// We avoid shipping external audio binaries by procedurally generating a
// stadium-cheer signal at runtime: a burst of band-passed white noise with
// an amplitude envelope + a rising pitch component (for "goal") or a
// descending pitch (for "opponent scores"). Total decoded footprint is
// zero, and the AudioContext is created lazily so it doesn't fire before
// the user has interacted with the page (browser autoplay policy).

let audioCtx = null;
let unlocked = false;
let muted = false;

function getCtx() {
  if (audioCtx) return audioCtx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  audioCtx = new Ctor();
  return audioCtx;
}

// Wire a one-time interaction listener so we're allowed to make sound.
if (typeof window !== "undefined") {
  const unlock = () => {
    if (unlocked) return;
    unlocked = true;
    const ctx = getCtx();
    if (ctx && ctx.state === "suspended") ctx.resume();
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    window.removeEventListener("touchstart", unlock);
  };
  window.addEventListener("pointerdown", unlock, { passive: true });
  window.addEventListener("keydown", unlock);
  window.addEventListener("touchstart", unlock, { passive: true });
}

export function setCheerMuted(m) {
  muted = !!m;
}

/**
 * Build a mono AudioBuffer holding a synthetic crowd cheer.
 * @param {AudioContext} ctx
 * @param {"cheer"|"groan"} kind
 */
function buildBuffer(ctx, kind) {
  const dur = kind === "cheer" ? 1.35 : 1.0;
  const sr = ctx.sampleRate;
  const len = Math.floor(sr * dur);
  const buffer = ctx.createBuffer(1, len, sr);
  const data = buffer.getChannelData(0);

  // Low-frequency modulation for "crowd" (varies amplitude in bursts).
  const modHz = kind === "cheer" ? 6.5 : 3.5;
  // Overall envelope: quick attack, slow decay.
  const attack = 0.06 * len;
  const decayStart = kind === "cheer" ? 0.35 * len : 0.25 * len;

  // Simple 1-pole lowpass state.
  let lp = 0;
  const alpha = kind === "cheer" ? 0.55 : 0.4;

  for (let i = 0; i < len; i++) {
    // White noise
    const noise = Math.random() * 2 - 1;
    // Lowpass so it doesn't sound like static
    lp = lp * (1 - alpha) + noise * alpha;

    // Envelope
    let env;
    if (i < attack) env = i / attack;
    else if (i < decayStart) env = 1.0;
    else env = 1 - (i - decayStart) / (len - decayStart);

    // Crowd modulation
    const mod = 0.65 + 0.35 * Math.sin((i / sr) * 2 * Math.PI * modHz);

    // Add a subtle pitched component that sweeps for character.
    const t = i / sr;
    const sweep =
      kind === "cheer"
        ? Math.sin(2 * Math.PI * (240 + 90 * t) * t) * 0.08
        : Math.sin(2 * Math.PI * (180 - 60 * t) * t) * 0.06;

    data[i] = (lp * 0.85 + sweep) * env * mod * 0.7;
  }

  return buffer;
}

const bufferCache = {};

/**
 * Play a cheer clip. Safe to call from any React effect / event.
 * @param {"player"|"cpu"} who
 */
export function playCheer(who) {
  if (muted) return;
  const ctx = getCtx();
  if (!ctx || ctx.state === "suspended") return;
  const kind = who === "player" ? "cheer" : "groan";
  const key = `${kind}-${ctx.sampleRate}`;
  let buf = bufferCache[key];
  if (!buf) {
    buf = buildBuffer(ctx, kind);
    bufferCache[key] = buf;
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const gain = ctx.createGain();
  gain.gain.value = who === "player" ? 0.6 : 0.5;
  src.connect(gain).connect(ctx.destination);
  src.start();
}

/**
 * Play a short referee whistle-blow at the given time offset.
 *
 * Synthesized with an FM-style pair of oscillators (a piercing ~2.5 kHz
 * carrier modulated by a fast trill) plus a quick amplitude envelope so it
 * reads as a stadium ref whistle rather than a beep. Two short toots feels
 * more natural than one long one.
 *
 * @param {number} [delayS=0]   Start delay from ctx.currentTime, in seconds.
 * @param {number} [durS=0.18]  Toot duration in seconds.
 */
export function playWhistle(delayS = 0, durS = 0.18) {
  if (muted) return;
  const ctx = getCtx();
  if (!ctx || ctx.state === "suspended") return;
  const start = ctx.currentTime + delayS;

  // Two short toots so it reads like a real ref cadence.
  const toots = [
    { at: start, dur: durS },
    { at: start + durS + 0.06, dur: durS * 0.85 },
  ];

  toots.forEach(({ at, dur }) => {
    const carrier = ctx.createOscillator();
    carrier.type = "sine";
    carrier.frequency.value = 2500;

    // Fast trill modulator (~30Hz)
    const trillOsc = ctx.createOscillator();
    trillOsc.type = "sine";
    trillOsc.frequency.value = 32;
    const trillGain = ctx.createGain();
    trillGain.gain.value = 180; // ±180Hz frequency modulation
    trillOsc.connect(trillGain).connect(carrier.frequency);

    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0, at);
    amp.gain.linearRampToValueAtTime(0.35, at + 0.015);
    amp.gain.linearRampToValueAtTime(0.35, at + dur - 0.03);
    amp.gain.exponentialRampToValueAtTime(0.001, at + dur);

    carrier.connect(amp).connect(ctx.destination);

    carrier.start(at);
    trillOsc.start(at);
    carrier.stop(at + dur + 0.02);
    trillOsc.stop(at + dur + 0.02);
  });
}


// Basketball net-swoosh — a very short high-pass filtered noise burst with
// a quick pitch drop. Reads as "ball snapping through the twine" and pairs
// naturally with the player-side crowd cheer we already fire on scoring.
export function playSwoosh() {
  if (muted) return;
  const ctx = getCtx();
  if (!ctx || !unlocked) return;
  const now = ctx.currentTime;
  const dur = 0.34;
  // White noise buffer — one shot, mono, 0.4s.
  const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.42), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.85;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  // Band-pass at ~5–8kHz with a quick downward sweep → that satisfying
  // "swwsh" tail rather than a flat hiss.
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.Q.value = 3.4;
  bp.frequency.setValueAtTime(7200, now);
  bp.frequency.exponentialRampToValueAtTime(2200, now + dur);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.5, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
  src.connect(bp).connect(gain).connect(ctx.destination);
  src.start(now);
  src.stop(now + dur + 0.02);
}

// Baseball crack-of-the-bat — a short filtered noise "thwack" with a
// millisecond-scale click transient. Played on every successful swing.
export function playBatCrack() {
  if (muted) return;
  const ctx = getCtx();
  if (!ctx || !unlocked) return;
  const now = ctx.currentTime;
  // Transient click — a very short square burst at ~900Hz
  const click = ctx.createOscillator();
  click.type = "square";
  click.frequency.setValueAtTime(900, now);
  click.frequency.exponentialRampToValueAtTime(120, now + 0.06);
  const cg = ctx.createGain();
  cg.gain.setValueAtTime(0.55, now);
  cg.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
  click.connect(cg).connect(ctx.destination);
  click.start(now);
  click.stop(now + 0.1);

  // Wood body — filtered noise burst layered on top
  const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.14), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.6;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(1400, now);
  lp.Q.value = 1.2;
  const wg = ctx.createGain();
  wg.gain.setValueAtTime(0, now);
  wg.gain.linearRampToValueAtTime(0.4, now + 0.005);
  wg.gain.exponentialRampToValueAtTime(0.001, now + 0.13);
  src.connect(lp).connect(wg).connect(ctx.destination);
  src.start(now);
  src.stop(now + 0.15);
}
